/**
 * Reassign by year… — move a calibre's linked refs to the revision their year
 * points at, after the calibre joined a revision line (ETA 7750 refs from
 * 1990 → 7750-1). Sibling to the AKA-based LineageReassignDialog.
 *
 * Suggestions come from the bulk importer's shared resolver (ref year → brand
 * founded when ≥ 2000 → current); see services/reassignByYear. Opt-in only:
 * nothing is ticked on open, "Accept all high-confidence" skips refs with a
 * curator signal, and at Apply each ref is re-read and skipped if it left this
 * calibre since. The ref's calibre TEXT is kept (legacyCalibreString off), as
 * in the AKA reassign.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import {
  findReferencesForCalibre,
  getReferencesByDocIds,
  getWatchBrandById,
  logAdminEvents,
  recomputeCalibreReferenceCountsFor,
  type CustomCalibreEntry,
} from '../../services/firebase';
import { revisionPickReason, type RevisionConfidence } from '../../services/calibreLineageLogic';
import { highConfidenceKeys, planReassignByYear, rowKey, type ReassignRef, type ReassignRow } from '../../services/reassignByYear';
import { pushCalibreDataToRefs } from './parentCalibrePush';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  calibre: CustomCalibreEntry;
  /** Every member of the calibre's line, itself included. */
  members: CustomCalibreEntry[];
  onReassigned?: () => void;
}

/** Refs hydrated per request; the progress count steps by this. */
const PAGE = 100;

const TONE: Record<RevisionConfidence, string> = {
  high: 'bg-green-50 text-green-800 border-green-200',
  medium: 'bg-amber-50 text-amber-800 border-amber-200',
  low: 'bg-gray-50 text-gray-700 border-gray-200',
};

export function ReassignByYearDialog({ isOpen, onClose, calibre, members, onReassigned }: Props) {
  const legacyMovement = useFeatureFlag('calibre.dualWriteLegacyMovement');
  const [rows, setRows] = useState<ReassignRow<CustomCalibreEntry>[] | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [accepted, setAccepted] = useState<Set<string>>(new Set());
  const [applying, setApplying] = useState(false);
  const byId = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);

  const load = useCallback(async () => {
    setRows(null);
    setAccepted(new Set());
    try {
      const { candidates } = await findReferencesForCalibre({ calibreId: calibre.id, mode: 'linked' });
      setProgress({ done: 0, total: candidates.length });
      const byBrand = new Map<string, string[]>();
      for (const c of candidates) byBrand.set(c.brandId, [...(byBrand.get(c.brandId) ?? []), c.refId]);
      const refs: ReassignRef[] = [];
      const foundedByBrand = new Map<string, number | undefined>();
      let done = 0;
      for (const [brandId, ids] of byBrand) {
        const brand = await getWatchBrandById(brandId);
        foundedByBrand.set(brandId, typeof brand?.founded === 'number' ? brand.founded : undefined);
        for (let i = 0; i < ids.length; i += PAGE) {
          const page = await getReferencesByDocIds(brandId, ids.slice(i, i + PAGE));
          for (const [refId, ref] of page) {
            if (ref.calibreId === calibre.id) refs.push({ brandId, refId, ref: ref as unknown as Record<string, unknown> });
          }
          done += Math.min(PAGE, ids.length - i);
          setProgress({ done, total: candidates.length });
        }
      }
      setRows(planReassignByYear({ calibre, members, refs, foundedByBrand }));
    } catch (err) {
      console.error('Failed to load linked references', err);
      toast.error(err instanceof Error ? err.message : 'Could not load references');
      setRows([]);
    }
  }, [calibre, members]);

  useEffect(() => {
    if (isOpen) void load();
  }, [isOpen, load]);

  const toggle = (key: string, on: boolean) =>
    setAccepted((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  const handleApply = async () => {
    if (!rows || accepted.size === 0) return;
    setApplying(true);
    try {
      const chosen = rows.filter((r) => accepted.has(rowKey(r)));
      const groups = new Map<string, ReassignRow<CustomCalibreEntry>[]>();
      for (const r of chosen) {
        const k = `${r.brandId}|${r.suggestion.member.id}`;
        groups.set(k, [...(groups.get(k) ?? []), r]);
      }
      const runToken = `reassign-by-year-${crypto.randomUUID()}`;
      const events: Parameters<typeof logAdminEvents>[0] = [];
      let moved = 0;
      let skipped = 0;
      for (const group of groups.values()) {
        const { brandId } = group[0];
        const target = byId.get(group[0].suggestion.member.id);
        if (!target) continue;
        const fresh = await getReferencesByDocIds(brandId, group.map((r) => r.refId));
        // Only refs still on THIS calibre move — one relinked since the scan is skipped.
        const refs = [...fresh.values()].filter((ref) => ref.calibreId === calibre.id);
        skipped += group.length - refs.length;
        if (refs.length === 0) continue;
        const { updatedRefs } = await pushCalibreDataToRefs(
          brandId,
          refs,
          target,
          // The calibre TEXT is the evidence trail: never rewritten by a reassignment.
          { legacyMovement, legacyCalibreString: false },
          runToken,
          { deferCalibreRecount: true },
        );
        moved += updatedRefs.length;
        const byRef = new Map(group.map((r) => [r.refId, r]));
        for (const ref of updatedRefs) {
          const row = byRef.get(ref.id);
          if (!row) continue;
          events.push({
            name: 'calibre_revision_reassigned',
            params: {
              refId: ref.id,
              brandId,
              fromCalibreId: calibre.id,
              toCalibreId: target.id,
              method: row.suggestion.method,
              confidence: row.suggestion.confidence,
            },
          });
        }
      }
      try {
        await recomputeCalibreReferenceCountsFor([calibre.id, ...new Set(chosen.map((r) => r.suggestion.member.id))]);
      } catch (err) {
        console.warn('Reassigned, but the reference recount failed', err);
      }
      void logAdminEvents(events);
      toast.success(
        `Moved ${moved} reference(s)${skipped ? ` · skipped ${skipped} that changed since the scan` : ''}`,
      );
      onReassigned?.();
      await load();
    } catch (err) {
      console.error('Reassign by year failed', err);
      toast.error(err instanceof Error ? err.message : 'Reassign failed');
    } finally {
      setApplying(false);
    }
  };

  const highKeys = rows ? highConfidenceKeys(rows) : new Set<string>();

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Reassign by year — ${calibre.name}`} size="xl">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          References on this calibre whose year points at another revision of its line: the ref&rsquo;s own year,
          else its brand&rsquo;s founded year (2000 or later), else the current revision. Nothing moves until you
          tick it. A ref&rsquo;s calibre text is kept as the evidence trail.
        </p>
        {rows === null ? (
          <p className="text-sm text-gray-500">
            Loading references…{progress && progress.total > 0 ? ` ${progress.done} / ${progress.total}` : ''}
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500">Every linked reference already fits this calibre.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="secondary" onClick={() => setAccepted(new Set(highKeys))} disabled={highKeys.size === 0}>
                Accept all high-confidence ({highKeys.size})
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setAccepted(new Set())}>
                Reject all
              </Button>
              <span className="text-xs text-gray-500">
                {accepted.size} of {rows.length} accepted
              </span>
            </div>
            <div className="max-h-[50vh] overflow-y-auto rounded border border-gray-200">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-50 text-gray-500">
                  <tr>
                    <th className="p-1.5 w-6" />
                    <th className="p-1.5 text-left font-medium">Reference</th>
                    <th className="p-1.5 text-left font-medium">Current → suggested</th>
                    <th className="p-1.5 text-left font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const key = rowKey(r);
                    const label = String(r.ref.reference ?? r.refId);
                    return (
                      <tr key={key} className="border-t border-gray-100 align-top">
                        <td className="p-1.5">
                          <input
                            type="checkbox"
                            aria-label={`Accept ${label}`}
                            checked={accepted.has(key)}
                            onChange={(e) => toggle(key, e.target.checked)}
                          />
                        </td>
                        <td className="p-1.5">
                          <span className="font-mono text-gray-900">{label}</span>
                          {typeof r.ref.model === 'string' && r.ref.model && <span className="text-gray-600"> · {r.ref.model}</span>}
                          <span className="block text-gray-400">{r.brandId}</span>
                        </td>
                        <td className="p-1.5 text-gray-700">
                          {calibre.name} → <span className="font-medium text-gray-900">{r.suggestion.member.name}</span>
                        </td>
                        <td className="p-1.5 space-y-1">
                          <span className={`inline-block rounded border px-1.5 py-0.5 ${TONE[r.suggestion.confidence]}`}>
                            {revisionPickReason(r.suggestion)} · {r.suggestion.confidence}
                          </span>
                          {r.curatorSignal && (
                            <span className="block rounded border border-purple-200 bg-purple-50 px-1.5 py-0.5 text-purple-800">
                              {r.curatorSignal}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
          <Button variant="primary" size="sm" onClick={() => void handleApply()} disabled={applying || accepted.size === 0}>
            {applying ? 'Applying…' : `Apply (${accepted.size})`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
